import { Divider, DividerProps } from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../theme/generated/tokens';
import { mergeSx, withAlpha } from '../../utils';

/** @deprecated `ShadowDivider` is deprecated — use `Divider` instead. */
export type ShadowDividerProps = Pick<
  DividerProps,
  'orientation' | 'sx' | 'className' | 'component'
> & {
  direction?: 'top' | 'bottom' | 'left' | 'right';
  opacity?: number;
  endOpacity?: number;
  size?: number;

  /** Make the divider position absolute */
  absolute?: boolean;
  /** Position divider at the bottom */
  bottom?: boolean;
  /** Position divider at the left */
  left?: boolean;
  /** Position divider at the right */
  right?: boolean;
  /** Position divider at the top */
  top?: boolean;
};

function getDegree(direction: ShadowDividerProps['direction']): string {
  switch (direction) {
    case 'top':
      return '0deg';
    case 'bottom':
      return '180deg';
    case 'left':
      return '-90deg';
    case 'right':
      return '90deg';
    default:
      return '';
  }
}

function boolToPosition(bool: boolean | undefined) {
  return bool ? 0 : undefined;
}

/**
 * @deprecated Shadow (gradient) dividers are being removed product-wide in
 * favor of plain dividers. Migrate to `Divider` from `@cast/design-system`.
 */
export const ShadowDivider = ({
  opacity = 1,
  endOpacity = 1,
  orientation = 'horizontal',
  direction = orientation === 'vertical' ? 'right' : 'bottom',
  size = 20,
  className,
  sx,
  absolute,
  top,
  bottom,
  left,
  right,
  ...props
}: ShadowDividerProps) => {
  const width = orientation === 'vertical' ? `${size}px` : '100%';
  const height = orientation === 'vertical' ? '100%' : `${size}px`;
  const deg = getDegree(direction);

  return (
    <Divider
      orientation={orientation}
      {...props}
      sx={mergeSx(
        {
          width,
          height,
          pointerEvents: 'none',
          border: 'none',
          background: `linear-gradient(${deg}, ${withAlpha(
            tokens.theme.gradient.shadowDividerStop1,
            opacity
          )} 0%, ${withAlpha(
            tokens.theme.gradient.shadowDividerStop2,
            endOpacity
          )} ${size}px)`,
        },
        {
          position: absolute ? 'absolute' : undefined,
          top: boolToPosition(top),
          bottom: boolToPosition(bottom),
          left: boolToPosition(left),
          right: boolToPosition(right),
        },
        sx
      )}
      className={clsx('DsShadowDivider-Root', className)}
    />
  );
};
